import React from "react";
import { graphql, useFragment } from "react-relay";

import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AppContext from "~/common/AppContext";
import { useRecordIconShouldBeVisible } from "~/meetingRecorder/RecordIcon";
import MenuLink, { type MenuLinkProps } from "~/nav/panels/Home/Links/MenuLink";
import { formatDate } from "~/nav/panels/Home/utils";
import { isBannerDismissed, RECURRING_1ON1_BANNER_KEY } from "~/stream/SeriesBanners/api";
import { Tooltip } from "~/ui";
import Avatar from "~/ui/avatar";

import GroupsMenu from "./GroupsMenu";

import type { OneOnOneLink_user$key as UserFragment } from "~/__generated__/OneOnOneLink_user.graphql";
import type { StreamLink_stream$data as Stream } from "~/__generated__/StreamLink_stream.graphql";

import styles from "~/nav/panels/shared/PanelSection.module.css";

export default function OneOnOneLink(
	props: Omit<MenuLinkProps, "to" | "innerRef" | "activeClassName"> & {
		user: UserFragment;
		stream?: Stream;
		innerRef?: React.Ref<HTMLAnchorElement>;
	},
) {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const { user: userProp, stream, innerRef, ...restProps } = props;

	const user = useFragment(
		graphql`
			fragment OneOnOneLink_user on User {
				id
				fullName
				image
				isActive
				isConfigured
				email
				hasReceivedInvites
				relationshipToViewer
				isGuest
				isTopUser

				activeRecurring1on1Event(onlyReportManager: true) {
					id
				}
			}
		`,
		userProp,
	);

	const nextEventOccurrence = stream?.linkedEvent?.nextOccurrence ?? null;
	const recordIconShouldBeVisible = useRecordIconShouldBeVisible(nextEventOccurrence);

	const userAvatar = React.useMemo(
		() => <Avatar user={user} size="small" hideIcon={recordIconShouldBeVisible} className={styles.avatarIcon} />,
		[user, recordIconShouldBeVisible],
	);

	const viewerId = React.useContext(AppContext).user?.id;
	const date = formatDate(nextEventOccurrence?.start);
	const title = stream?.displayName ?? user.fullName;

	let recurring1on1Text = "";
	if (
		["DIRECT_REPORT", "MANAGER"].includes(user.relationshipToViewer || "") &&
		!user.activeRecurring1on1Event &&
		user.isActive &&
		!user.isGuest &&
		!isBannerDismissed(RECURRING_1ON1_BANNER_KEY, user.id)
	) {
		const relation = user.relationshipToViewer === "MANAGER" ? "manager" : "report";
		recurring1on1Text = `You do not have a recurring 1-on-1 with your ${relation}`;
	}

	return (
		<div className={styles.wrapper}>
			<MenuLink
				{...restProps}
				innerRef={innerRef}
				to={stream?.url || `/1on1s/${viewerId}/${user.id}/`}
				activeClassName={styles.active}
				data-test-id="1on1-link"
				showRecordIcon={recordIconShouldBeVisible}
				icon={userAvatar}
				iconTooltip={recordIconShouldBeVisible ? "This 1-on-1 will be recorded" : "1-on-1"}
			>
				<span title={title} className={styles.title}>
					{title}
					{recurring1on1Text && (
						<Tooltip
							className={styles.tooltipCenterAligned}
							content={recurring1on1Text}
							placement="top"
							maxWidth="8.625rem"
						>
							<FontAwesomeIcon icon={faExclamationCircle} className={styles.warningIcon} />
						</Tooltip>
					)}
				</span>
				{!isMenuOpen && date && <span className={styles.date}>{date}</span>}
			</MenuLink>
			<GroupsMenu stream={stream} isMenuOpen={isMenuOpen} toggleOpen={setIsMenuOpen} />
		</div>
	);
}
